import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
  CircularProgress
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import { useTheme as useThemeV5 } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import PeopleIcon from "@mui/icons-material/People";
import DownloadIcon from "@mui/icons-material/GetApp";
import AddIcon from "@mui/icons-material/Add";
import ContactsIcon from "@mui/icons-material/Contacts";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactListDialog from "../../components/ContactListDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import planilhaExemplo from "../../assets/planilha.xlsx";
import { AuthContext } from "../../context/Auth/AuthContext";

// ---------------------------------------------------------------------------
// Global Styles (mesmo do Dashboard)
// ---------------------------------------------------------------------------
const globalStyles = `
  @keyframes pulse-ring {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.6; }
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 1; }
  }
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = globalStyles;
  document.head.appendChild(styleSheet);
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------
const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_CONTACTLISTS": {
      const contactLists = action.payload;
      const newContactLists = [];
      contactLists.forEach((contactList) => {
        const idx = state.findIndex((u) => u.id === contactList.id);
        if (idx !== -1) {
          state[idx] = contactList;
        } else {
          newContactLists.push(contactList);
        }
      });
      return [...state, ...newContactLists];
    }
    case "UPDATE_CONTACTLIST": {
      const contactList = action.payload;
      const idx = state.findIndex((u) => u.id === contactList.id);
      if (idx !== -1) {
        state[idx] = contactList;
        return [...state];
      }
      return [contactList, ...state];
    }
    case "DELETE_CONTACTLIST": {
      const contactListId = action.payload;
      const idx = state.findIndex((u) => u.id === contactListId);
      if (idx !== -1) state.splice(idx, 1);
      return [...state];
    }
    case "RESET":
      return [];
    default:
      return state;
  }
};

// ---------------------------------------------------------------------------
// ContactLists
// ---------------------------------------------------------------------------
const ContactLists = () => {
  const themeV5 = useThemeV5();
  const themeV4 = useThemeV4();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";

  // --- State ---
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedContactList, setSelectedContactList] = useState(null);
  const [deletingContactList, setDeletingContactList] = useState(null);
  const [contactListModalOpen, setContactListModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [contactLists, dispatch] = useReducer(reducer, []);

  // ---------------------------------------------------------------------------
  // Fetch
  // ---------------------------------------------------------------------------
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delay = setTimeout(() => {
      const fetchContactLists = async () => {
        try {
          const { data } = await api.get("/contact-lists/", {
            params: { searchParam, pageNumber }
          });
          dispatch({ type: "LOAD_CONTACTLISTS", payload: data.records });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContactLists();
    }, 500);
    return () => clearTimeout(delay);
  }, [searchParam, pageNumber]);

  // ---------------------------------------------------------------------------
  // Socket
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const companyId = user.companyId;
    const onContactListEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTLIST", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACTLIST", payload: +data.id });
      }
    };
    socket.on(`company-${companyId}-ContactList`, onContactListEvent);
    return () => socket.off(`company-${companyId}-ContactList`, onContactListEvent);
  }, [socket, user.companyId]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleOpenContactListModal = () => {
    setSelectedContactList(null);
    setContactListModalOpen(true);
  };

  const handleCloseContactListModal = () => {
    setSelectedContactList(null);
    setContactListModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditContactList = (contactList) => {
    setSelectedContactList(contactList);
    setContactListModalOpen(true);
  };

  const handleDeleteContactList = async (contactListId) => {
    try {
      await api.delete(`/contact-lists/${contactListId}`);
      toast.success(i18n.t("contactLists.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContactList(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const goToContacts = (id) => {
    history.push(`/contact-lists/${id}/contacts`);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 3 },
        bgcolor: "#f8fafc"
      }}
    >
      <Container maxWidth="xl">

        {/* Header */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                bgcolor: PRIMARY_MAIN,
                "& svg": { width: 28, height: 28, color: "#fff" }
              }}
            >
              <ContactsIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{
                  color: "text.primary",
                  mb: 0.5,
                  letterSpacing: "-0.5px",
                  fontSize: { xs: "1.5rem", md: "1.875rem" }
                }}
              >
                {i18n.t("contactLists.title")}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
                Gerenciamento de listas de contatos
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={2}>
            {/* Search */}
            <TextField
              placeholder={i18n.t("contacts.searchPlaceholder")}
              type="search"
              size="small"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.disabled", fontSize: 20 }} />
                  </InputAdornment>
                )
              }}
              sx={{
                width: { xs: 200, sm: 280 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "#fff",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  "& fieldset": { border: "none" },
                  "&:hover": { 
                    borderColor: PRIMARY_MAIN,
                    bgcolor: "action.hover"
                  },
                  "&.Mui-focused": { 
                    borderColor: PRIMARY_MAIN,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)"
                  }
                },
                "& .MuiOutlinedInput-input": {
                  py: 1.25,
                  fontSize: "0.875rem",
                  fontWeight: 500
                }
              }}
            />

            {/* Add button */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenContactListModal}
              sx={{
                bgcolor: PRIMARY_MAIN,
                color: "#fff",
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                "&:hover": {
                  bgcolor: PRIMARY_DARK,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.18)"
                }
              }}
            >
              {i18n.t("contactLists.buttons.add")}
            </Button>
          </Stack>
        </Stack>

        {/* Tabela */}
        <Paper
          elevation={0}
          onScroll={handleScroll}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}
        >
          {/* Cabeçalho da tabela */}
          <Box sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: "1.125rem" }}>
                {searchParam ? `Resultados da busca` : "Todas as listas de contatos"}
              </Typography>
              <Chip
                label={`${contactLists.length} ${contactLists.length !== 1 ? "listas" : "lista"}`}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  bgcolor: "rgba(148, 163, 184, 0.08)",
                  color: "text.secondary",
                  border: "1px solid",
                  borderColor: "rgba(148, 163, 184, 0.2)"
                }}
              />
            </Stack>
          </Box>

          {/* Grid */}
          <TableContainer sx={{ maxHeight: "calc(100vh - 280px)" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                      color: "text.secondary",
                      letterSpacing: "0.5px",
                      bgcolor: "background.paper"
                    }}
                  >
                    {i18n.t("contactLists.table.name")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                      color: "text.secondary",
                      letterSpacing: "0.5px",
                      bgcolor: "background.paper"
                    }}
                  >
                    {i18n.t("contactLists.table.contacts")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                      color: "text.secondary",
                      letterSpacing: "0.5px",
                      bgcolor: "background.paper"
                    }}
                  >
                    {i18n.t("contactLists.table.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {contactLists.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" color="text.secondary">
                        Nenhuma lista de contatos encontrada
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  contactLists.map((contactList) => (
                    <TableRow
                      key={contactList.id}
                      sx={{
                        "&:hover": {
                          bgcolor: "action.hover",
                          transition: "background-color 0.2s"
                        }
                      }}
                    >
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.875rem" }}>
                          {contactList.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                          <Chip
                            icon={<PeopleIcon sx={{ fontSize: 14, color: `${PRIMARY_MAIN} !important` }} />}
                            label={contactList.contactsCount || 0}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              bgcolor: `${PRIMARY_MAIN}10`,
                              color: PRIMARY_MAIN,
                              border: "1px solid",
                              borderColor: `${PRIMARY_MAIN}20`,
                              px: 1
                            }}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Baixar Planilha Exemplo">
                            <IconButton
                              size="small"
                              component="a"
                              href={planilhaExemplo}
                              download="planilha.xlsx"
                              sx={{
                                color: "text.secondary",
                                "&:hover": { color: "#10b981", bgcolor: "rgba(16, 185, 129, 0.08)" }
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Ver Contatos">
                            <IconButton
                              size="small"
                              onClick={() => goToContacts(contactList.id)}
                              sx={{
                                color: "text.secondary",
                                "&:hover": { color: PRIMARY_MAIN, bgcolor: `${PRIMARY_MAIN}10` }
                              }}
                            >
                              <PeopleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => handleEditContactList(contactList)}
                              sx={{
                                color: "text.secondary",
                                "&:hover": { color: PRIMARY_MAIN, bgcolor: `${PRIMARY_MAIN}10` }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Deletar">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setConfirmModalOpen(true);
                                setDeletingContactList(contactList);
                              }}
                              sx={{
                                color: "text.secondary",
                                "&:hover": { color: "error.main", bgcolor: "rgba(239, 68, 68, 0.08)" }
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}

                {loading && <TableRowSkeleton columns={3} />}
              </TableBody>
            </Table>
          </TableContainer>

          {loading && contactLists.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Paper>
      </Container>

      {/* Modais */}
      <ConfirmationModal
        title={
          deletingContactList
            ? `${i18n.t("contactLists.confirmationModal.deleteTitle")} ${deletingContactList.name}?`
            : ""
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteContactList(deletingContactList.id)}
      >
        {i18n.t("contactLists.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <ContactListDialog
        open={contactListModalOpen}
        onClose={handleCloseContactListModal}
        aria-labelledby="form-dialog-title"
        contactListId={selectedContactList && selectedContactList.id}
      />
    </Box>
  );
};

export default ContactLists;